import React, { useCallback, useRef, useState } from 'react';
import { Box, Icon, Link, Spinner } from '@amzn/awsui-components-core';
import { EAHuddleService } from 'src/services/EAHuddleService';
import { logger } from 'src/logger';
import { MAX_ATTACHMENTS } from '../../constants/EAHuddleConstants';
import './MeetingAttachments.scss';

export interface MeetingAttachment {
  fileName: string;
  s3Uri: string;
}

interface MeetingAttachmentsProps {
  attachments: MeetingAttachment[];
  onAttachmentsChange: (attachments: MeetingAttachment[]) => void;
  meetingId: string | null;
  organizerAlias: string;
  isReadOnly?: boolean;
}

export const MeetingAttachments: React.FC<MeetingAttachmentsProps> = ({
  attachments,
  onAttachmentsChange,
  meetingId,
  organizerAlias,
  isReadOnly = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      const remaining = MAX_ATTACHMENTS - attachments.length;
      if (remaining <= 0 || !meetingId) return;

      const existingByName = new Map(attachments.map((a) => [a.fileName, a]));
      const filesToUpload = files.slice(0, remaining + existingByName.size);
      if (filesToUpload.length === 0) return;

      setIsUploading(true);
      let updated = [...attachments];
      try {
        for (const file of filesToUpload) {
          const result = await EAHuddleService.uploadMeetingAttachment(file, organizerAlias, meetingId);
          if (result.success && result.s3Uri && result.fileName) {
            const existing = existingByName.get(result.fileName);
            if (existing) {
              await EAHuddleService.deleteAttachment(existing.s3Uri);
              updated = updated.map((a) => (a.fileName === result.fileName ? { fileName: result.fileName, s3Uri: result.s3Uri! } : a));
            } else if (updated.length < MAX_ATTACHMENTS) {
              updated = [...updated, { fileName: result.fileName, s3Uri: result.s3Uri }];
            }
          } else {
            logger.error('Failed to upload attachment', { fileName: file.name, error: result.error });
          }
        }
        onAttachmentsChange(updated);
      } finally {
        setIsUploading(false);
      }
    },
    [meetingId, organizerAlias, attachments, onAttachmentsChange]
  );

  const handleRemove = useCallback(
    async (s3Uri: string) => {
      onAttachmentsChange(attachments.filter((a) => a.s3Uri !== s3Uri));
      await EAHuddleService.deleteAttachment(s3Uri);
    },
    [attachments, onAttachmentsChange]
  );

  const handleNativeFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) handleFileUpload(files);
      e.target.value = '';
    },
    [handleFileUpload]
  );

  const atLimit = attachments.length >= MAX_ATTACHMENTS;

  const renderAttachmentTokens = (removable: boolean) =>
    attachments.length > 0 ? (
      <div className="meeting-attachments-tokens">
        {attachments.map((attachment) => (
          <div key={attachment.s3Uri} className="meeting-attachments-token">
            <span className="meeting-attachments-token-icon">
              <Icon name="file" size="small" />
            </span>
            <span
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => EAHuddleService.downloadAttachment(attachment.s3Uri)}
              onKeyDown={(e) => e.key === 'Enter' && EAHuddleService.downloadAttachment(attachment.s3Uri)}
            >
              {attachment.fileName}
            </span>
            {removable && (
              <button
                className="meeting-attachments-token-remove"
                onClick={() => handleRemove(attachment.s3Uri)}
                aria-label={`Remove ${attachment.fileName}`}
              >
                <Icon name="close" size="small" />
              </button>
            )}
          </div>
        ))}
      </div>
    ) : null;

  if (isReadOnly) return renderAttachmentTokens(false);

  return (
    <>
      <div className="meeting-attachments-row">
        <span className="meeting-attachments-icon">
          <Icon name="upload" />
        </span>
        <Link
          onFollow={(e) => {
            e.preventDefault();
            if (!atLimit) fileInputRef.current?.click();
          }}
          variant={atLimit ? 'secondary' : undefined}
        >
          Attach Files
        </Link>
        <span className="meeting-attachments-hint">
          {atLimit
            ? "You've added the maximum number of files. Remove one to add another."
            : `Supported formats: .pdf, .docx, .xlsx, .pptx, .jpg, .jpeg, .png | Limit: ${MAX_ATTACHMENTS} files, 10 MB each`}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png"
          className="meeting-attachments-file-input-hidden"
          onChange={handleNativeFileChange}
        />
      </div>
      {isUploading && (
        <div className="meeting-attachments-uploading">
          <Spinner size="normal" />
          <Box fontSize="body-s" color="text-body-secondary">
            Uploading...
          </Box>
        </div>
      )}
      {renderAttachmentTokens(true)}
    </>
  );
};
